import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { daysUntilDue, formatDueDateLocal, isOpenPastDue } from '../../utils/dueDate';
import { applyInspectionDue } from '../../utils/inspectionDueDate';
import { getJobRowStyle, getStatusFilterOptionClass } from '../../utils/jobStatusColor';
import { useAuthStore } from '../../stores/authStore';
import { Calendar, Filter, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, Search, X } from 'lucide-react';
import PageHeading from '../../components/PageHeading';
import { enrichJobsWithWorkerNames } from '../../utils/workerDisplay';
import { getInspectionsListHeading } from '../../constants/jobModel';
import { getInspectionPeerNav } from '../../navigation/inspectionNavigation';
import { useDueQueueData, dueQueueRowsToListRows } from '../../hooks/useDueQueueData';
import {
  groupInspectionsByBuilding,
  getSsChipTextClass,
} from '../../utils/groupInspectionsByBuilding';

function buildingRowClass(g) {
  if (g.status_unified) {
    const style = getJobRowStyle(
      g.status_unified === 'OVERDUE' ? 'OVERDUE' : g.primary_status || g.status_unified,
      g.days,
      g.status_unified === 'PENDING' && g.primary_status === 'Due soon'
    );
    return style.row || 'bg-white';
  }
  return 'bg-white';
}

function Jobs() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFromUrl = searchParams.get('status');
  const urgentFromUrl = searchParams.get('urgent') === '1';
  const overdueFromUrl = searchParams.get('overdue') === '1';
  const dueWorkFromUrl = searchParams.get('due') === '1';
  const [statusFilter, setStatusFilter] = useState(() => {
    if (overdueFromUrl) return 'OVERDUE';
    if (dueWorkFromUrl) return 'DUE_WORK';
    if (statusFromUrl === 'ALL' || statusFromUrl === 'OPEN') return 'ALL';
    if (statusFromUrl) return statusFromUrl;
    return 'DUE_WORK';
  });
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [sortKey, setSortKey] = useState('date');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchText, setSearchText] = useState('');
  const fromDashboard =
    statusFromUrl != null ||
    urgentFromUrl ||
    overdueFromUrl ||
    dueWorkFromUrl ||
    statusFilter === 'OVERDUE' ||
    statusFilter === 'DUE_WORK';

  const statusFilterOptions = [
    { value: 'DUE_WORK', label: 'Due work (start here)' },
    { value: 'OVERDUE', label: 'Over Due' },
    { value: 'ALL', label: 'Open (active jobs)' },
    { value: 'NOTIFIED', label: 'Notified' },
    { value: 'SCHEDULED', label: 'Scheduled' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'AFTER_PAPERWORK', label: 'After paperwork' },
    { value: 'REPAIRING', label: 'Repairing' },
    { value: 'COMPLETED', label: 'Completed (archive)' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'WAITING', label: 'Waiting' },
  ];
  const currentStatusLabel = statusFilterOptions.find((o) => o.value === statusFilter)?.label || statusFilter;

  const handleSort = (key) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };
  const SortIcon = ({ column }) => {
    if (sortKey !== column) return <ArrowUpDown className="w-3.5 h-3.5 ml-1 inline opacity-50" />;
    return sortOrder === 'asc' ? <ArrowUp className="w-3.5 h-3.5 ml-1 inline" /> : <ArrowDown className="w-3.5 h-3.5 ml-1 inline" />;
  };

  useEffect(() => {
    if (!statusFromUrl && !urgentFromUrl && !overdueFromUrl && !dueWorkFromUrl) {
      setSearchParams({ due: '1' }, { replace: true });
      if (statusFilter !== 'DUE_WORK') setStatusFilter('DUE_WORK');
      return;
    }
    if (dueWorkFromUrl && statusFilter !== 'DUE_WORK') setStatusFilter('DUE_WORK');
    else if (overdueFromUrl && statusFilter !== 'OVERDUE') setStatusFilter('OVERDUE');
    else if (statusFromUrl === 'ALL' || statusFromUrl === 'OPEN') {
      if (statusFilter !== 'ALL') setStatusFilter('ALL');
    } else if (statusFromUrl && statusFilter !== statusFromUrl && !overdueFromUrl && !dueWorkFromUrl) {
      setStatusFilter(statusFromUrl);
    }
  }, [statusFromUrl, overdueFromUrl, dueWorkFromUrl, urgentFromUrl]);

  useEffect(() => {
    if (user?.role === 'WORKER') {
      navigate('/', { replace: true });
    }
  }, [user?.role, navigate]);

  const { data: jobsData, isLoading } = useQuery({
    queryKey: ['jobs', statusFilter, urgentFromUrl, overdueFromUrl],
    queryFn: async () => {
      const params = new URLSearchParams({ page: '1', limit: '500' });
      if (statusFilter === 'OVERDUE' || overdueFromUrl) params.append('overdue', '1');
      else if (statusFilter !== 'ALL' && statusFilter !== 'DUE_WORK') params.append('status', statusFilter);
      if (urgentFromUrl && statusFilter !== 'OVERDUE') params.append('urgent', '1');
      // Due work: load open jobs so building groups can enrich queue rows
      if (statusFilter === 'DUE_WORK' || dueWorkFromUrl) {
        /* default API excludes COMPLETED */
      }
      const response = await api.get(`/jobs?${params.toString()}`);
      return response.data;
    },
    enabled: !!user && user.role !== 'WORKER',
  });

  const { data: workersList = [] } = useQuery({
    queryKey: ['workers', 'for-jobs-list'],
    queryFn: async () => {
      const response = await api.get('/workers', { params: { limit: 1000, status: 'all' } });
      return Array.isArray(response.data?.data) ? response.data.data : [];
    },
    enabled: !!user && user.role !== 'WORKER',
    staleTime: 60_000,
  });

  const workerNameById = useMemo(() => {
    const map = {};
    (workersList || []).forEach((w) => {
      if (!w?.id) return;
      map[String(w.id).trim()] =
        (w.name && String(w.name).trim()) || (w.username && String(w.username).trim()) || String(w.id).trim();
    });
    return map;
  }, [workersList]);

  const { data: buildingsDueSoonData } = useQuery({
    queryKey: ['buildings', 'due-soon', 30],
    queryFn: async () => (await api.get('/buildings/due-soon?days=30')).data,
    enabled: !!user && user.role !== 'WORKER',
  });

  const { rows: dueQueueRows } = useDueQueueData();

  const jobsRaw = useMemo(
    () => enrichJobsWithWorkerNames(jobsData?.data || [], workerNameById).map((job) => applyInspectionDue(job)),
    [jobsData, workerNameById]
  );

  const jobsById = useMemo(() => {
    const map = new Map();
    jobsRaw.forEach((j) => {
      if (j?.id == null) return;
      map.set(j.id, j);
      map.set(String(j.id), j);
    });
    return map;
  }, [jobsRaw]);

  const jobsAligned = useMemo(() => {
    if (overdueFromUrl || statusFilter === 'OVERDUE') {
      return jobsRaw.filter((j) => isOpenPastDue(j.status, j.period_due));
    }
    if (urgentFromUrl && statusFilter !== 'OVERDUE') {
      return jobsRaw.filter((j) => {
        if (j.status === 'COMPLETED' || j.status === 'REJECTED') return false;
        const d = daysUntilDue(j.period_due);
        return d != null && d >= 0 && d <= 30;
      });
    }
    if (statusFilter === 'ALL') {
      return jobsRaw.filter((j) => j.status !== 'COMPLETED' && j.status !== 'REJECTED');
    }
    return jobsRaw;
  }, [jobsRaw, overdueFromUrl, urgentFromUrl, statusFilter]);

  const buildingsDueSoon = buildingsDueSoonData?.data || [];

  const merged = useMemo(() => {
    const fromQueue = (buckets) =>
      dueQueueRowsToListRows(
        dueQueueRows.filter((r) => buckets.includes(r.bucket)),
        jobsById
      );

    if (statusFilter === 'DUE_WORK' || dueWorkFromUrl) {
      const q = fromQueue(['overdue', 'urgent']);
      if (q.length > 0) return q;
      // Fallback: open jobs due within 30d or overdue (so list is not blank if queue empty)
      return jobsAligned
        .filter((j) => {
          const d = daysUntilDue(j.period_due);
          return d != null && d <= 30;
        })
        .map((j) => ({ ...j, _type: 'job' }));
    }
    if (overdueFromUrl || statusFilter === 'OVERDUE') {
      const q = fromQueue(['overdue']);
      if (q.length > 0) return q;
      return jobsAligned
        .filter((j) => isOpenPastDue(j.status, j.period_due))
        .map((j) => ({ ...j, _type: 'job' }));
    }
    if (urgentFromUrl && statusFilter !== 'OVERDUE' && statusFilter !== 'DUE_WORK') {
      return fromQueue(['urgent']);
    }

    const buildingIdsWithJob = new Set(jobsAligned.map((j) => j.building_id));
    const showBuildingDueRows = statusFilter === 'ALL' || statusFilter === 'PENDING';
    const buildingDueRows = !showBuildingDueRows
      ? []
      : buildingsDueSoon
          .filter((b) => {
            if (buildingIdsWithJob.has(b.id)) return false;
            const d = daysUntilDue(b.due_date);
            return d != null && d >= 0 && d <= 30;
          })
          .map((b) => ({
            _type: 'building_due',
            id: `building-${b.id}`,
            building_id: b.id,
            building_name: b.name,
            address: b.address,
            company_id: b.company_id,
            due_date: b.due_date,
            ss_codes: b.ss_codes || [],
            ss_list: b.ss_list || [],
            application_approved: !!b.application_approved,
          }));
    return [...jobsAligned.map((j) => ({ ...j, _type: 'job' })), ...buildingDueRows];
  }, [
    jobsAligned,
    buildingsDueSoon,
    overdueFromUrl,
    statusFilter,
    urgentFromUrl,
    dueWorkFromUrl,
    dueQueueRows,
    jobsById,
  ]);

  const buildingRows = useMemo(() => {
    let groups = [];
    try {
      groups = groupInspectionsByBuilding(merged);
    } catch (e) {
      console.error('[Inspections] group by building failed', e);
      groups = [];
    }
    const q = searchText.trim().toLowerCase();
    if (q) {
      groups = groups.filter(
        (g) =>
          g.inspection_label.toLowerCase().includes(q) ||
          String(g.building_name).toLowerCase().includes(q) ||
          String(g.building_id).includes(q)
      );
    }
    return [...groups].sort((a, b) => {
      const mul = sortOrder === 'asc' ? 1 : -1;
      if (sortKey === 'date') {
        const dA = a.due_date ? new Date(a.due_date).getTime() : 0;
        const dB = b.due_date ? new Date(b.due_date).getTime() : 0;
        return mul * (dA - dB);
      }
      if (sortKey === 'inspection') {
        return mul * String(a.inspection_label).localeCompare(String(b.inspection_label));
      }
      if (sortKey === 'status') {
        return mul * String(a.status_label).localeCompare(String(b.status_label));
      }
      return 0;
    });
  }, [merged, searchText, sortKey, sortOrder]);

  if (user?.role === 'WORKER') {
    return <div className="text-center py-12 text-gray-500">Redirecting…</div>;
  }

  if (isLoading) {
    return <div className="text-center py-12">Loading inspections...</div>;
  }

  const listHeading = getInspectionsListHeading({
    overdue: overdueFromUrl || statusFilter === 'OVERDUE',
    urgent: urgentFromUrl && statusFilter !== 'OVERDUE' && statusFilter !== 'DUE_WORK',
    statusFilter,
  });

  /** Building SS list — do not carry list filters (due/overdue/urgent/status). */
  const openBuilding = (g, opts = {}) => {
    const params = new URLSearchParams();
    if (opts.ss) params.set('ss', opts.ss);
    if (opts.iqp) params.set('iqp', opts.iqp);
    if (opts.workerId) params.set('workerId', String(opts.workerId));
    const qs = params.toString();
    navigate(`/jobs/building/${encodeURIComponent(g.building_id)}${qs ? `?${qs}` : ''}`);
  };

  return (
    <div>
      <PageHeading
        title={listHeading.title}
        trail={[]}
        infoTitle={listHeading.infoTitle}
        infoChildren={
          <div className="space-y-2 text-sm text-gray-700">
            <p className="whitespace-pre-line">{listHeading.infoBody}</p>
            <p>
              Click the <strong>building name</strong> for all SS, an <strong>SS chip</strong> for that system, or an{' '}
              <strong>IQP name</strong> for that inspector&apos;s SS on the building. Schedule is on each SS detail.
              Mixed SS statuses keep a white row; when all match, the row takes that status colour.
            </p>
          </div>
        }
        subPages={getInspectionPeerNav('/jobs', { canCreate: user?.role !== 'WORKER' })}
      />

      {fromDashboard && (
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            onClick={() => {
              setSearchParams({ status: 'ALL' });
              setStatusFilter('ALL');
            }}
            className="text-sm font-medium text-primary-600 hover:text-primary-800"
          >
            Show all inspections
          </button>
        </div>
      )}

      <div className="bg-card rounded-lg shadow p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="w-5 h-5 text-gray-400 shrink-0" aria-hidden />
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setStatusDropdownOpen((o) => !o)}
              className={`inline-flex items-center gap-2 border rounded-lg px-4 py-2 min-w-[180px] justify-between ${getStatusFilterOptionClass(statusFilter)} ${statusFilter === 'OVERDUE' ? 'border-purple-400 bg-purple-50' : 'border-gray-300 bg-white'}`}
            >
              <span>{currentStatusLabel}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {statusDropdownOpen && (
              <>
                <div className="fixed inset-0 z-10" aria-hidden onClick={() => setStatusDropdownOpen(false)} />
                <ul className="absolute left-0 top-full mt-1 z-20 py-1 w-full min-w-[200px] bg-white border rounded-lg shadow-lg max-h-72 overflow-auto">
                  {statusFilterOptions.map((opt) => (
                    <li key={opt.value}>
                      <button
                        type="button"
                        onClick={() => {
                          setStatusFilter(opt.value);
                          setSearchParams(
                            opt.value === 'OVERDUE'
                              ? { overdue: '1' }
                              : opt.value === 'DUE_WORK'
                                ? { due: '1' }
                                : opt.value === 'ALL'
                                  ? { status: 'ALL' }
                                  : { status: opt.value }
                          );
                          setStatusDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 ${statusFilter === opt.value ? 'font-semibold bg-gray-50' : ''}`}
                      >
                        {opt.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
            <input
              type="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search building or SS…"
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            />
            {searchText && (
              <button
                type="button"
                onClick={() => setSearchText('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <p className="text-sm text-gray-600 shrink-0">
            <span className="font-medium text-gray-900">{buildingRows.length}</span> building
            {buildingRows.length === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      <div className="bg-card rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                <button type="button" onClick={() => handleSort('inspection')} className="inline-flex items-center hover:text-gray-700">
                  Inspection
                  <SortIcon column="inspection" />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[120px]">
                <button type="button" onClick={() => handleSort('date')} className="inline-flex items-center hover:text-gray-700">
                  Due
                  <SortIcon column="date" />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[200px]">
                Default IQP
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {buildingRows.length > 0 ? (
              buildingRows.map((g) => {
                const rowBg = buildingRowClass(g);
                const dateStyle = getJobRowStyle(
                  g.status_unified === 'OVERDUE' ? 'OVERDUE' : g.primary_status || 'PENDING',
                  g.days,
                  false
                );
                const ssEntries = Array.isArray(g.ss_entries) ? g.ss_entries : [];
                const iqpPeople = Array.isArray(g.iqp_people) ? g.iqp_people : [];
                return (
                  <tr key={g.building_key} className={rowBg}>
                    <td className="px-4 py-3 align-top">
                      <div className="text-sm leading-snug flex flex-wrap items-baseline gap-x-1 gap-y-1">
                        <button
                          type="button"
                          onClick={() => openBuilding(g)}
                          className="font-semibold text-gray-900 hover:text-primary-700 hover:underline text-left"
                          title="Open all SS for this building"
                        >
                          {g.building_name}
                        </button>
                        {ssEntries.length > 0 ? <span className="text-gray-400">—</span> : null}
                        {ssEntries.map((e, i) => (
                          <span key={`${g.building_key}-${e.ss_code}`} className="inline-flex items-baseline">
                            {i > 0 ? <span className="text-gray-400 mr-1">,</span> : null}
                            <button
                              type="button"
                              onClick={() => openBuilding(g, { ss: e.ss_code })}
                              className={`font-medium hover:underline text-left ${getSsChipTextClass(
                                e.status,
                                e.due_date
                              )}`}
                              title={`Open ${e.ss_code}`}
                            >
                              {e.chip}
                            </button>
                          </span>
                        ))}
                        {!ssEntries.length ? (
                          <span className="text-gray-400 text-xs">No SS</span>
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {g.ss_count} SS
                        {g.status_unified
                          ? ` · unified ${g.status_label || g.status_unified}`
                          : g.ss_count > 1
                            ? ' · mixed status'
                            : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap align-top">
                      <div className="flex items-center pointer-events-none select-none">
                        <Calendar className="w-4 h-4 text-gray-400 mr-1.5" />
                        <span className={`text-sm ${dateStyle.date}`}>
                          {g.due_date ? formatDueDateLocal(g.due_date) : '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800 align-top">
                      {iqpPeople.length ? (
                        <div className="flex flex-wrap gap-x-1 gap-y-1">
                          {iqpPeople.map((p, i) => (
                            <span key={`${g.building_key}-iqp-${p.name}`} className="inline-flex">
                              {i > 0 ? <span className="text-gray-400 mr-1">,</span> : null}
                              <button
                                type="button"
                                onClick={() =>
                                  openBuilding(g, {
                                    iqp: p.name,
                                    workerId: p.worker_id || undefined,
                                  })
                                }
                                className="font-medium text-primary-700 hover:underline text-left"
                                title={`SS for ${p.name} on this building`}
                              >
                                {p.name}
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">…</span>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={3} className="px-6 py-12 text-center text-gray-500">
                  No buildings with inspections in this filter
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Jobs;
